class AccordionElement extends HTMLElement {
  constructor() {
    super();
    this.details = this.closest("details");
    this.summary = this.details.querySelector("summary");

    this._resetHeight();

    if (!this.id) {
      this.id = `accordion-${Math.random().toString(36).slice(2)}`;
    }

    this.summary.setAttribute("aria-controls", this.id);

    // Bind event handlers
    this.onClick = this.onClick.bind(this);
    this.onDetailsClick = this.onDetailsClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);

    // Bind click handler to the full details block for a larger tap target.
    this.details.addEventListener("click", this.onDetailsClick);

    // Listen for window resize event
    window.addEventListener("resize", this.onResize);
    this.details.addEventListener("transitionend", this.onTransitionEnd);
  }

  _getDetailsVerticalPadding() {
    const styles = window.getComputedStyle(this.details);
    return (
      parseFloat(styles.paddingTop || "0") +
      parseFloat(styles.paddingBottom || "0")
    );
  }

  _getClosedHeight() {
    return this.summary.scrollHeight + this._getDetailsVerticalPadding() - 3;
  }

  _getOpenHeight() {
    return this.details.scrollHeight;
  }

  _closeOtherAccordions() {
    const accordionElements = document.querySelectorAll(
      "accordion-element[data-accordion-content]",
    );

    accordionElements.forEach((accordionElement) => {
      if (accordionElement !== this && accordionElement.details?.open) {
        accordionElement._close();
      }
    });
  }

  _open() {
    this._closeOtherAccordions();

    const closedHeight = this._getClosedHeight();
    this.details.setAttribute("data-opening", "true");
    this.details.setAttribute("open", "true");
    this.details.style.height = `${closedHeight}px`;

    requestAnimationFrame(() => {
      if (!this.details.hasAttribute("data-opening")) return;
      this.details.style.height = `${this._getOpenHeight()}px`;
    });
  }

  _close() {
    const openHeight = this.details.getBoundingClientRect().height;

    this.details.style.height = `${openHeight}px`;
    this.details.removeAttribute("data-opening");
    this.details.setAttribute("data-closing", "true");

    requestAnimationFrame(() => {
      if (!this.details.hasAttribute("data-closing")) return;
      this.details.style.height = `${this._getClosedHeight()}px`;
    });
  }

  _resetHeight() {
    this.details.style.height = `${this._getClosedHeight()}px`;
  }

  onClick(e) {
    e.preventDefault();
    if (!this.details.open) {
      this._open();
    } else {
      this._close();
    }
  }

  onDetailsClick(e) {
    const interactiveTarget = e.target.closest(
      "a, button, input, select, textarea, label",
    );

    if (interactiveTarget) {
      return;
    }

    this.onClick(e);
  }

  onResize() {
    if (this.details.hasAttribute("data-opening")) {
      this.details.style.height = `${this._getOpenHeight()}px`;
    } else if (this.details.hasAttribute("data-closing")) {
      this.details.style.height = `${this._getClosedHeight()}px`;
    } else if (this.details.open) {
      this.details.style.height = "auto";
    } else {
      this._resetHeight();
    }
  }

  onTransitionEnd(e) {
    if (e.target !== this.details || e.propertyName !== "height") return;

    if (this.details.hasAttribute("data-opening")) {
      this.details.removeAttribute("data-opening");
      this.details.style.height = "auto";
      return;
    }

    if (this.details.hasAttribute("data-closing")) {
      this.details.removeAttribute("open");
      this.details.removeAttribute("data-closing");
      this._resetHeight();
    }
  }

  // Clean up event listener when element is removed from the DOM
  disconnectedCallback() {
    this.details.removeEventListener("click", this.onDetailsClick);
    this.details.removeEventListener("transitionend", this.onTransitionEnd);
    window.removeEventListener("resize", this.onResize);
  }
}

if (!customElements.get("accordion-element")) {
  customElements.define("accordion-element", AccordionElement);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.headerCart = document.querySelector("header-cart");
    this.triggerButton = document.querySelector(
      '[aria-controls="cart-drawer"]',
    );
    this.lastFocusedElement = null;
    this.justOpenedAt = 0;

    this.openDrawer = this.openDrawer.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentFocusIn = this.onDocumentFocusIn.bind(this);
    this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this);

    this.addEventListener("click", this.onClick);
  }

  connectedCallback() {
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("focusin", this.onDocumentFocusIn);
    document.addEventListener("keydown", this.onDocumentKeyDown);
    this.syncTriggerExpanded();
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("focusin", this.onDocumentFocusIn);
    document.removeEventListener("keydown", this.onDocumentKeyDown);
  }

  isOpen() {
    return this.getAttribute("inert") === null;
  }

  getCloseButton() {
    return this.querySelector("#close-drawer");
  }

  syncTriggerExpanded() {
    if (!this.triggerButton) {
      this.triggerButton = document.querySelector(
        '[aria-controls="cart-drawer"]',
      );
    }

    if (this.triggerButton) {
      this.triggerButton.setAttribute(
        "aria-expanded",
        this.isOpen() ? "true" : "false",
      );
    }
  }

  async onClick(e) {
    // Close button inside the drawer
    const closeTrigger = e.target.closest("#close-drawer");
    if (closeTrigger) {
      this.closeDrawer();
      if (this.headerCart) {
        this.headerCart.changeState();
      }
      return;
    }

    // "Normal remove" link, extended to also remove add-ons
    const removeLink = e.target.closest("[data-remove-line]");
    if (removeLink) {
      e.preventDefault();

      const lineKey = removeLink.dataset.lineKey;
      const uuid = removeLink.dataset.addonUuid;

      await this.removeLineAndAddons(lineKey, uuid);
      return;
    }
  }

  onDocumentClick(e) {
    // Ignore immediately after opening to avoid closing from the same click
    if (Date.now() - this.justOpenedAt < 50) {
      return;
    }

    if (!this.isOpen()) {
      return;
    }

    // Clicked outside the drawer → close it
    if (!this.contains(e.target)) {
      this.closeDrawer();
      if (this.headerCart) {
        this.headerCart.changeState();
      }
    }
  }

  onDocumentFocusIn(e) {
    if (!this.isOpen()) {
      return;
    }

    if (this.contains(e.target)) {
      return;
    }

    const closeButton = this.getCloseButton();
    if (closeButton) {
      closeButton.focus();
    }
  }

  onDocumentKeyDown(e) {
    if (e.key !== "Escape" || !this.isOpen()) {
      return;
    }

    this.closeDrawer();
    if (this.headerCart) {
      this.headerCart.changeState();
    }
  }

  openDrawer() {
    this.dispatchEvent(new CustomEvent("cart-drawer-transition-start"));
    this.justOpenedAt = Date.now();
    this.lastFocusedElement = document.activeElement;

    if (this.headerCart) {
      this.headerCart.changeState();
    }

    this.removeAttribute("inert");
    this.syncTriggerExpanded();

    const closeButton = this.getCloseButton();
    if (closeButton) {
      closeButton.focus();
    }

    this.dispatchEvent(new CustomEvent("cart-drawer-state-change"));
  }

  closeDrawer() {
    this.dispatchEvent(new CustomEvent("cart-drawer-transition-start"));
    this.setAttribute("inert", "");
    this.syncTriggerExpanded();

    if (this.lastFocusedElement instanceof HTMLElement) {
      this.lastFocusedElement.focus();
    }

    this.dispatchEvent(new CustomEvent("cart-drawer-state-change"));
  }

  /**
   * Re-render the cart drawer by fetching the cart-drawer section.
   * Also updates the header cart item count.
   */
  async update() {
    const res = await fetch("/?section_id=cart-drawer");
    const text = await res.text();
    const html = document.createElement("div");
    html.innerHTML = text;

    const nextCartDrawer = html.querySelector("cart-drawer");
    if (nextCartDrawer) {
      this.innerHTML = nextCartDrawer.innerHTML;
    }

    const cartItemCounts = document.querySelectorAll(".cart-item-count");
    const itemCountHelper = html.querySelector("#item-count-helper");
    if (cartItemCounts && itemCountHelper) {
      cartItemCounts.forEach((cartItemCount) => {
        cartItemCount.textContent = itemCountHelper.textContent || "";
      });
    }
  }

  /**
   * Add a product to the cart from a form or FormData.
   * Used for the main product "Add to cart".
   */
  async addToCart(form) {
    const requestBody = form instanceof FormData ? form : new FormData(form);

    await fetch("/cart/add", {
      method: "post",
      body: requestBody,
    });

    await this.update();
    this.openDrawer();
  }

  /**
   * Add an object to the cart using the /cart/add.js JSON endpoint.
   */
  async addObjectToCart(object) {
    await fetch("/cart/add.js", {
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(object),
    });

    await this.update();
    this.openDrawer();
  }

  /**
   * "Normal remove" for a line:
   * - Always removes the clicked line (lineKey)
   * - ALSO removes any add-on lines linked by _addon_uuid to the uuid of the clicked line
   */
  async removeLineAndAddons(lineKey, uuid) {
    if (!lineKey) return;

    // 1. Fetch the current cart
    const cartResponse = await fetch("/cart.js");
    const cartData = await cartResponse.json();

    const productUuidStr = uuid ? String(uuid) : null;

    let keysToRemove = [lineKey];

    if (productUuidStr) {
      const addonLines = cartData.items.filter((item) => {
        const isAddon = item?.properties?._is_addon === "true";
        const uuid = item?.properties?._addon_uuid;
        return isAddon && String(uuid) === productUuidStr;
      });

      keysToRemove = keysToRemove.concat(addonLines.map((item) => item.key));
    }

    // 2. Remove main line + any addon lines
    for (const key of keysToRemove) {
      await fetch("/cart/change.js", {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: key,
          quantity: 0,
        }),
      });
    }

    // 3. Refresh the drawer
    await this.update();

    // 4. Close the drawer if the cart is empty
    const updatedResponse = await fetch("/cart.js");
    const updatedCart = await updatedResponse.json();
    if (updatedCart.item_count === 0) {
      this.closeDrawer();
    }
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

class ProductLightbox extends HTMLElement {
  constructor() {
    super();

    this.lastFocusedElement = null;
    this.observer = null;
    this.imageRatios = new Map();

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.goToImage = this.goToImage.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onIntersect = this.onIntersect.bind(this);

    this.addEventListener("click", this.onClick);
  }

  connectedCallback() {
    document.addEventListener("keydown", this.onKeyDown);

    this.observer = new IntersectionObserver(this.onIntersect, {
      root: this,
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });
    this.querySelectorAll("[data-lightbox-index]").forEach((image) => {
      this.observer.observe(image);
    });
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.onKeyDown);
    this.observer?.disconnect();
  }

  isOpen() {
    return this.getAttribute("inert") === null;
  }

  onClick(e) {
    if (e.target.closest("#close-lightbox")) {
      this.close();
    }
  }

  onKeyDown(e) {
    if (e.key === "Escape" && this.isOpen()) {
      this.close();
    }
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      this.imageRatios.set(
        entry.target.dataset.lightboxIndex,
        entry.isIntersecting ? entry.intersectionRatio : 0,
      );
    });

    let activeIndex = null;
    let maxRatio = 0;
    this.imageRatios.forEach((ratio, index) => {
      if (ratio > maxRatio) {
        maxRatio = ratio;
        activeIndex = index;
      }
    });

    if (activeIndex !== null) {
      this.setActiveThumb(activeIndex);
    }
  }

  setActiveThumb(index) {
    this.querySelectorAll("[data-lightbox-thumb-index]").forEach((thumb) => {
      thumb.setAttribute(
        "aria-current",
        thumb.dataset.lightboxThumbIndex === index ? "true" : "false",
      );
    });
  }

  goToImage(index) {
    this.querySelector(`[data-lightbox-index="${index}"]`)?.scrollIntoView({
      block: "start",
    });
    this.setActiveThumb(String(index));
  }

  open(index) {
    this.lastFocusedElement = document.activeElement;
    this.removeAttribute("inert");
    this.querySelector("#close-lightbox")?.focus();

    if (typeof index === "number") {
      this.goToImage(index);
    }
  }

  close() {
    this.setAttribute("inert", "");
    if (this.lastFocusedElement instanceof HTMLElement) {
      this.lastFocusedElement.focus();
    }
  }
}

if (!customElements.get("product-lightbox")) {
  customElements.define("product-lightbox", ProductLightbox);
}

class CartItem extends HTMLElement {
  constructor() {
    super();
    // bind click event to button id decrease
    this.decreaseButton = this.querySelector("button[id='decrease']");
    this.increaseButton = this.querySelector("button[id='increase']");
    // this.quantity = parseInt(this.querySelector("p[id='quantity']").innerHTML);

    this.pid = this.getAttribute("data-product-id");
    // this.handleQuantity = this.handleQuantity.bind(this);

    //this.decreaseButton.addEventListener("click", this.handleQuantity);
    //this.increaseButton.addEventListener("click", this.handleQuantity);
  }

  // async handleQuantity(e) {
  //   console.log(e.target.id);
  //   if (e.target.id === "decrease") {
  //     if (parseInt(this.quantity) === 1) {
  //       console.log("too low");
  //     }
  //     if (parseInt(this.quantity) > 1) {
  //       document
  //         .querySelector("cart-drawer")
  //         .updateQuantity(this.pid, this.quantity - 1);
  //       console.log("decrease it by 1 bro");
  //     }
  //   }
  //   if (e.target.id === "increase") {
  //     document
  //       .querySelector("cart-drawer")
  //       .updateQuantity(this.pid, this.quantity + 1);
  //   }
  //   e.preventDefault();
  //   console.log(this.pid, this.quantity);
  // }
}

if (!customElements.get("cart-item")) {
  customElements.define("cart-item", CartItem);
}

class SlideShow extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.setupMarkup();

    if (this.hasAttribute("data-thumb-id")) {
      setTimeout(() => {
        this.initSwiper();
      }, 50);
    } else {
      this.initSwiper();
    }
  }
  setupMarkup() {
    // Create base Swiper structure
    const swiperContainer = document.createElement("div");
    swiperContainer.classList.add("swiper");

    if (this.getAttribute("data-id")) {
      swiperContainer.id = this.getAttribute("data-id");
    }

    const wrapper = document.createElement("div");
    wrapper.classList.add("swiper-wrapper");

    // Move all existing children into swiper-slide wrappers
    Array.from(this.children).forEach((child) => {
      const slide = document.createElement("div");
      slide.classList.add("swiper-slide");
      if (child.id) {
        slide.id = child.id;
      }
      slide.appendChild(child);
      wrapper.appendChild(slide);
    });

    swiperContainer.appendChild(wrapper);

    // if scrollbar is enabled, add scrollbar element
    if (this.getAttribute("data-scrollbar") === "true") {
      const scrollbar = document.createElement("div");
      scrollbar.classList.add("swiper-scrollbar");
      swiperContainer.appendChild(scrollbar);
    }

    // Clear original content and append structured swiper
    this.innerHTML = "";
    this.appendChild(swiperContainer);
  }

  // slideTo: takes in id of slide and slides to it
  slideTo(id) {
    if (!this.swiper) return;

    const slideIndex = Array.from(this.swiper.slides).findIndex(
      (slide) => slide.id === id,
    );

    if (slideIndex === -1) {
      console.warn(`Slide "${id}" not found`);
      return;
    }

    console.log({
      requestedId: id,
      slideIndex,
      targetSlideId: this.swiper.slides[slideIndex]?.id,
    });

    this.swiper.slideTo(slideIndex);
  }

  initSwiper() {
    const swiperEl = this.querySelector(".swiper");

    if (swiperEl) {
      // Detect navigation buttons placed inside the custom element
      // Detect navigation buttons: prefer IDs passed via `buttons` attribute
      const buttonsId = this.getAttribute("data-buttons");
      let prevBtn = null;
      let nextBtn = null;

      if (buttonsId) {
        prevBtn = document.getElementById(`prev-${buttonsId}`);
        nextBtn = document.getElementById(`next-${buttonsId}`);
      }

      // Fallback to buttons inside the custom element
      if (!prevBtn && !nextBtn) {
        prevBtn = this.querySelector(".swiper-button-prev");
        nextBtn = this.querySelector(".swiper-button-next");
      }

      const options = {
        slidesPerView: this.getAttribute("data-slides-per-view-mobile")
          ? this.getAttribute("data-slides-per-view-mobile")
          : this.getAttribute("data-slides-per-view")
            ? this.getAttribute("data-slides-per-view")
            : 1,
        spaceBetween: this.getAttribute("data-gap-mobile")
          ? this.getAttribute("data-gap-mobile")
          : this.getAttribute("data-gap") ||
            this.getAttribute("data-space-between") ||
            0,
        speed: 500,
        centeredSlides: this.getAttribute("data-centered") === "true",
        autoplay: {
          delay: 3000,
          disableOnInteraction: true,
          enabled: this.getAttribute("data-autoplay") !== "false",
        },
        loopFillGroupWithBlank: true,
        mousewheel: {
          forceToAxis: true,
        },
        loop: this.getAttribute("data-loop") === "true",
        effect: this.getAttribute("data-effect") || "slide",
      };

      const scrollbarEl = this.querySelector(".swiper-scrollbar");
      if (this.getAttribute("data-scrollbar") === "true" && scrollbarEl) {
        options.scrollbar = {
          enabled: true,
          el: scrollbarEl,
          draggable: false,
        };
      }

      if (
        this.getAttribute("data-gap-mobile") &&
        this.getAttribute("data-gap")
      ) {
        options.breakpoints = options.breakpoints || {};
        options.breakpoints[768] = {
          ...(options.breakpoints[768] || {}),
          spaceBetween: this.getAttribute("data-gap"),
        };
      }

      if (
        this.getAttribute("data-slides-per-view-mobile") &&
        this.getAttribute("data-slides-per-view")
      ) {
        options.breakpoints = options.breakpoints || {};
        options.breakpoints = {
          ...(options.breakpoints || {}),
          768: {
            ...(options.breakpoints[768] || {}),
            slidesPerView: this.getAttribute("data-slides-per-view"),
          },
        };
      }

      const thumbId = this.getAttribute("data-thumb-id");
      const thumbSwiper = thumbId
        ? document.getElementById(thumbId)?.swiper
        : null;

      if (thumbSwiper) {
        options.thumbs = { swiper: thumbSwiper };
      }

      // If arrows exist, wire them into Swiper navigation
      if (prevBtn || nextBtn) {
        options.navigation = {
          prevEl: prevBtn || undefined,
          nextEl: nextBtn || undefined,
        };
      }

      this.swiper = new Swiper(swiperEl, options);

      // if thumbs, make sure the thumb active slide is also the active swiper slide
      if (thumbSwiper) {
        this.swiper.on("slideChange", () => {
          thumbSwiper.slideTo(this.swiper.activeIndex);
        });

        thumbSwiper.on("slideChange", () => {
          this.swiper.slideTo(thumbSwiper.activeIndex);
        });
      }

      console.log("Swiper initialized with options:", options);
    }
  }
}

if (!customElements.get("swiper-gallery")) {
  customElements.define("swiper-gallery", SlideShow);
}

class SizeChart extends HTMLElement {
  constructor() {
    super();

    console.log("sizing....", this.getAttribute("data-size-chart"));

    // Parse the JSON data from the `data-size-chart` attribute
    this.sizeChart = JSON.parse(this.getAttribute("data-size-chart") || "[]");

    console.log(this.sizeChart, "size chart data");

    // Create a shadow DOM for encapsulation
    const shadow = this.attachShadow({ mode: "open" });

    // Apply styles and add the table to the shadow DOM
    const style = document.createElement("style");
    style.textContent = `
      * {
        font-family: "Univers Pro", Helvetica, Helvetica Neue, Arial, sans-serif;
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 100%;
        color: #000000;
        text-align: left;
      }


      table {
        width: 100%;
        outline: 0;
        border-collapse: collapse;
      }

      th, td {
        width: 1%;
        padding-left: 8px;
        padding-right: 8px;
      }

      thead, tr {
        border-bottom: 1px solid #000000;
      }

      tr:last-child {
        border-bottom: 0px;
      }

      th {
        padding-bottom: 8px;
      }

      td {
        padding: 8px 8px;
      }
      
      td:first-child, th:first-child {
        padding-left: 0px;
      }

      td:last-child, th:last-child {
        padding-right: 0px;
      }

      tr:last-child td {
        padding-bottom: 0px;
      

    `;

    // Generate the table and append it to the shadow DOM
    const table = this.generateTable();
    shadow.appendChild(style);
    shadow.appendChild(table);
  }

  // Method to generate the table based on JSON data
  generateTable() {
    const table = document.createElement("table");

    // Create headers from the first item in the data array
    if (this.sizeChart.length > 0) {
      const headers = Object.values(this.sizeChart[0]);
      const thead = document.createElement("thead");
      thead.classList.add("table-header");

      const headerRow = document.createElement("tr");
      headers.forEach((headerText) => {
        const th = document.createElement("th");
        th.textContent = headerText;
        headerRow.appendChild(th);
      });

      thead.appendChild(headerRow);
      table.appendChild(thead);

      // Create table body with data
      const tbody = document.createElement("tbody");
      this.sizeChart.slice(1).forEach((rowData) => {
        const row = document.createElement("tr");

        Object.values(rowData).forEach((cellData) => {
          const cell = document.createElement("td");
          cell.textContent = cellData;
          row.appendChild(cell);
        });

        tbody.appendChild(row);
      });
      table.appendChild(tbody);
    }

    return table;
  }
}

// Define the custom element if it’s not already defined
if (!customElements.get("size-chart")) {
  customElements.define("size-chart", SizeChart);
}

class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll(".js-tab-button")];
    this.panels = [...this.querySelectorAll(".js-tab-panel")];
    this.outer = this.querySelector(".outer");

    if (!this.buttons.length || !this.panels.length) return;

    this.setActiveTab(this.buttons[0]);

    this.addEventListener("click", this.onClick);
    this.addEventListener("keydown", this.onKeyDown);

    this._onResize = this.onResize.bind(this);
    window.addEventListener("resize", this._onResize);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    this.removeEventListener("keydown", this.onKeyDown);

    window.removeEventListener("resize", this._onResize);
    clearTimeout(this.resizeTimeout);
  }

  setActiveTab(button) {
    const targetId = button.dataset.tabTarget;
    const panel = this.querySelector(`#${CSS.escape(targetId)}`);

    if (!panel) return;

    this.buttons.forEach((btn) => {
      const active = btn === button;
      btn.setAttribute("aria-selected", active);
      btn.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((p) => {
      p.setAttribute("aria-hidden", p !== panel);
    });

    this.updateHeight();
  }

  updateHeight() {
    const activePanel = this.querySelector(
      ".js-tab-panel[aria-hidden='false']",
    );

    if (!activePanel) return;

    this.outer.style.height = `${activePanel.scrollHeight}px`;
  }

  onResize() {
    const activePanel = this.querySelector(
      ".js-tab-panel[aria-hidden='false']",
    );
    if (!activePanel) return;

    this.panels.forEach((panel) => {
      if (panel !== activePanel) {
        panel.style.height = `0px`;
      }
    });

    // Let the browser lay everything out naturally while resizing
    this.outer.style.height = "auto";

    clearTimeout(this.resizeTimeout);

    this.resizeTimeout = setTimeout(() => {
      this.updateHeight();
      this.panels.forEach((panel) => {
        panel.style.height = "fit-content";
      });
    }, 150);
  }

  onClick(e) {
    const button = e.target.closest(".js-tab-button");
    if (!button) return;

    this.setActiveTab(button);
  }

  onKeyDown(e) {
    const current = e.target.closest(".js-tab-button");
    if (!current) return;

    const index = this.buttons.indexOf(current);
    let next = null;

    switch (e.key) {
      case "ArrowLeft":
      case "ArrowUp":
        next = index === 0 ? this.buttons.length - 1 : index - 1;
        break;

      case "ArrowRight":
      case "ArrowDown":
        next = index === this.buttons.length - 1 ? 0 : index + 1;
        break;

      case "Home":
        next = 0;
        break;

      case "End":
        next = this.buttons.length - 1;
        break;

      default:
        return;
    }

    e.preventDefault();
    this.buttons[next].focus();
    this.setActiveTab(this.buttons[next]);
  }
}

if (!customElements.get("product-tabs")) {
  customElements.define("product-tabs", ProductTabs);
}
